import { Chart } from 'chart.js';
import { arrayFlat } from 'common/utilities/core/array';
import { getTextWidth } from 'common/utilities/core/string';
import theme from 'core/utilities/theme';
import React, {
  createRef, useCallback, useEffect, useRef,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

function getCursorPosition(canvas: HTMLCanvasElement, event: MouseEvent) {
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  return { x, y };
}

export const getDistanceTreeItemTree = (items: DistanceTreeItem[], item: DistanceTreeItem): DistanceTreeItem[] => {
  const children = items.filter((i) => i.parentId === item.id);
  return [item, ...arrayFlat(children.map((i) => getDistanceTreeItemTree(items, i)))];
};

export interface Legend {
  min: string;
  max: string;
  text: string;
  position: number;
}

interface Node {
  item: DistanceTreeItem;
  x: number;
  y: number;
  nodePath: Path2D;
  leafPath?: Path2D;
}

export interface DistanceTreeItem {
  id: number;
  parentId?: number;
  label?: string;
  /** ideally all values should have more or less the same length */
  labelExtra?: string;
  /**
   * only for end leafs as the tree is built up from there
   * X position when orientation is horizontal, Y position when orientation is vertical
   */
  position?: number;
  /** percentage of canvas dimension */
  distance: number;
  nodeDistance: number;
}

export interface ComponentProps {
  id?: string;
  width: number;
  height: number;
  orientation: 'horizontal' | 'vertical';
  direction: 'default' | 'opposite';
  items: DistanceTreeItem[];
  selectedItems: number[];
  onClick?: (item: DistanceTreeItem) => void;
  onHover?: (item: DistanceTreeItem) => void;
  /** labels are only supported when orientation=vertical and direction=default */
  showLabel?: boolean;
  hideLabelBorder?: boolean;
  showLabelExtra?: boolean;
  leafDistance: number;
  color: string;
  selectedColor: string;
  legend?: Legend;
  endLinesToEdge?: boolean;
}

function DistanceTree({
  id, width, height, orientation, direction, items, selectedItems,
  onClick, onHover, showLabel, hideLabelBorder, showLabelExtra, leafDistance, color, selectedColor,
  legend, endLinesToEdge,
}: ComponentProps) {
  /** Diameter of a node point */
  const diameter = 6;
  const border = 1;
  const borderHover = 2;
  const padding = 2;
  const rectBorderRadius = 2;
  const paddingAfterRect = 8;

  const textHeight = leafDistance - padding - padding; // padding top and bottom

  const font = [
    '400',
    `${textHeight}px`,
    theme.typography.fontFamily as string,
  ].join(' ');

  const leafItems = items.filter((i) => i.position !== undefined);

  const getLabelWidth = useCallback((label: string) => (
    getTextWidth(label || '', font, '0.00938em') + padding + padding
  ), [font]);
  const longestLabelWidth = leafItems.length === 0 ? 0 : Math.max(...(leafItems.map((i) => (
    getLabelWidth(i.label || '')
  )))) || 0;
  const longestLabelExtraWidth = leafItems.length === 0 ? 0 : Math.max(...(leafItems.map((i) => (
    getTextWidth(i.labelExtra || '', font, '0.00938em') + paddingAfterRect
  ))));

  const availableWidth = orientation === 'vertical'
    ? (
      width
      - diameter
      - (showLabel ? longestLabelWidth : 0)
      - (showLabel && showLabelExtra ? longestLabelExtraWidth : 0)
    )
    : width;
  const availableHeight = orientation === 'horizontal' ? height - diameter : height;

  let topOffset = 0;
  if (orientation === 'horizontal') {
    topOffset += diameter / 2;
    if (showLabel) topOffset += textHeight;
  }

  const canvasRef = createRef<HTMLCanvasElement>();
  const nodes = useRef<Node[]>([]);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);

  /**
   * Pointer gives back a transparent path which is larger than the node for the user to interact with
   */
  const drawNode = useCallback((x: number, y: number, pointer?: boolean) => {
    if (!ctx.current) return undefined;

    const nodeDiameter = pointer ? diameter * 3 : diameter;
    let adjustedX = orientation === 'horizontal' ? x - (nodeDiameter / 2) : x;
    let adjustedY = orientation === 'vertical' ? y - (nodeDiameter / 2) : y;
    if (orientation === 'vertical' && pointer) adjustedX -= nodeDiameter / 3;
    if (orientation === 'horizontal' && pointer) adjustedY -= nodeDiameter / 3;

    const path = new Path2D();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((path as any).roundRect) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (path as any).roundRect(adjustedX, adjustedY, nodeDiameter, nodeDiameter, [nodeDiameter / 2]);
    } else {
      path.rect(adjustedX, adjustedY, nodeDiameter, nodeDiameter);
    }
    ctx.current.fillStyle = pointer ? 'transparent' : color;
    ctx.current.fill(path);

    return path;
  }, [color, orientation]);

  const drawLabel = useCallback((node: Node, hover?: boolean) => {
    if (!ctx.current || !node.item.label) return undefined;

    const rectWidth = longestLabelWidth;
    const rectHeight = leafDistance - 3; // 1px border top, 1px border bottom, 1px spacing
    const rectStartX = node.x + diameter - 1;
    const rectStartY = node.y - (rectHeight / 2);

    // Add yellow background to the rectangle when selected
    if (selectedItems.includes(node.item.id)) {
      ctx.current.fillStyle = selectedColor;
      ctx.current.fillRect(rectStartX, rectStartY, rectWidth, rectHeight);
    }

    // Rectangle
    const path = new Path2D();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((path as any).roundRect) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (path as any).roundRect(rectStartX, rectStartY, rectWidth, rectHeight, [rectBorderRadius]);
    } else {
      path.rect(rectStartX, rectStartY, rectWidth, rectHeight);
    }
    ctx.current.lineWidth = hover ? borderHover : border;
    ctx.current.strokeStyle = color;
    if (!hideLabelBorder) ctx.current.stroke(path);

    // Label
    ctx.current.fillStyle = color;
    ctx.current.font = font;
    if (orientation === 'vertical') {
      ctx.current.fillText(
        node.item.label,
        node.x + diameter + 1,
        // text is written from the bottom left, 2px for padding, 1px for border, 1px spacing
        node.y + (textHeight / 2) - padding - border + 1,
      );
    }
    if (orientation === 'horizontal') {
      const labelWidth = getLabelWidth(node.item.label);
      ctx.current.fillText(
        node.item.label,
        node.x + (diameter / 2) - (labelWidth / 2),
        // 2px for padding, 1px spacing
        node.y - 2 - 1,
      );
    }

    // Label extra
    if (node.item.labelExtra && showLabelExtra) {
      ctx.current.font = font;
      ctx.current.fillText(
        node.item.labelExtra,
        rectStartX + rectWidth + paddingAfterRect,
        node.y + (textHeight / 2) - padding - border,
      );
    }

    return path;
  }, [
    orientation, color, font, leafDistance, longestLabelWidth, selectedColor,
    selectedItems, showLabelExtra, textHeight, hideLabelBorder, getLabelWidth,
  ]);

  const drawLine = useCallback((fromNode: Node, toNode?: Node, hover?: boolean) => {
    if (!ctx.current) return undefined;

    ctx.current.save();
    ctx.current.beginPath();

    ctx.current.lineWidth = hover ? borderHover : border;
    ctx.current.strokeStyle = color;

    if (toNode) {
      // Draw a line between two nodes
      if (orientation === 'horizontal') {
        ctx.current.moveTo(fromNode.x, fromNode.y + (diameter / 2));
        ctx.current.lineTo(fromNode.x, toNode.y + (diameter / 2));
        ctx.current.lineTo(toNode.x, toNode.y + (diameter / 2));
      }

      if (orientation === 'vertical') {
        ctx.current.moveTo(fromNode.x + (diameter / 2), fromNode.y);
        ctx.current.lineTo(toNode.x + (diameter / 2), fromNode.y);
        ctx.current.lineTo(toNode.x + (diameter / 2), toNode.y);
      }
    } else {
      // Draw a line between a node and the edge
      ctx.current.strokeStyle = Chart.defaults.borderColor as string;

      if (orientation === 'horizontal') {
        ctx.current.moveTo(fromNode.x, fromNode.y - (showLabel ? textHeight + 4 : 0));
        ctx.current.lineTo(fromNode.x, 0);
      }

      if (orientation === 'vertical') {
        ctx.current.moveTo(fromNode.x + (showLabel ? getLabelWidth(fromNode.item.label || '') + 4 : 0), fromNode.y);
        ctx.current.lineTo(width, fromNode.y);
      }
    }

    ctx.current.stroke();
    ctx.current.restore();

    return undefined;
  }, [color, getLabelWidth, orientation, showLabel, textHeight, width]);

  const createItem = useCallback((item: DistanceTreeItem, hoveredItems?: DistanceTreeItem[]) => {
    if (nodes.current.some((p) => p.item.id === item.id)) return;

    const childNodes = nodes.current.filter((p) => p.item.parentId === item.id);
    if (childNodes.length !== 2) return;
    const minDistanceBetweenNodes = orientation === 'horizontal' ? 0 : leafDistance / 2;

    let x = (childNodes[0].x + childNodes[1].x) / 2;
    if (orientation === 'vertical' && direction === 'default') {
      x = item.distance === 0 ? 0 : (availableWidth * (item.distance / 100));
    }
    if (orientation === 'vertical' && direction === 'opposite') {
      x = item.distance === 0 ? availableWidth : availableWidth - (availableWidth * (item.distance / 100));
    }

    // When a parent node is too close to its children nodes, move it a bit
    if (
      orientation === 'vertical'
      && (childNodes[0].x - x < minDistanceBetweenNodes || childNodes[1].x < minDistanceBetweenNodes)
    ) {
      if (direction === 'default') x -= minDistanceBetweenNodes;
      if (direction === 'opposite') x += minDistanceBetweenNodes;
    }

    let y = (childNodes[0].y + childNodes[1].y) / 2;
    if (orientation === 'horizontal' && direction === 'default') {
      y = item.distance === 0
        ? availableHeight
        : topOffset + availableHeight - ((availableHeight * (item.distance / 100)));
    }
    if (orientation === 'horizontal' && direction === 'opposite') {
      y = item.distance === 0 ? 0 : ((availableHeight * (item.distance / 100)));
    }

    // When a parent node is too close to its children nodes, move it a bit
    if (
      orientation === 'horizontal'
      && (childNodes[0].y - y < minDistanceBetweenNodes || childNodes[1].y < minDistanceBetweenNodes)
    ) {
      if (direction === 'default') y -= minDistanceBetweenNodes;
      if (direction === 'opposite') y += minDistanceBetweenNodes;
    }

    const hover = hoveredItems?.some((i) => i.id === item.id) || false;

    // Draw visible node
    drawNode(x, y);

    // Draw pointer node (transparent, a bit bigger to ease the click)
    const nodePath = drawNode(x, y, true);
    if (!nodePath) return;
    const node: Node = {
      item, x, y, nodePath,
    };
    nodes.current.push(node);

    // Draw line with square angle between children nodes to node
    drawLine(childNodes[0], node, hover && hoveredItems?.some((i) => i.id === childNodes[0].item.id));
    drawLine(childNodes[1], node, hover && hoveredItems?.some((i) => i.id === childNodes[1].item.id));

    // Continue with parent node
    if (item.parentId === undefined) return;
    const parentItem = items.find((i) => i.id === item.parentId);

    if (parentItem) createItem(parentItem, hoveredItems);
  }, [
    availableHeight, availableWidth, direction, drawLine, drawNode, items, leafDistance, orientation, topOffset,
  ]);

  const drawLegend = useCallback(() => {
    if (!ctx.current || !legend) return;

    ctx.current.beginPath();

    ctx.current.lineWidth = 1;
    ctx.current.strokeStyle = color;

    if (orientation === 'horizontal') {
      // lines
      const initialX = legend.position + textHeight;
      const initialXEnd = initialX + 5;
      ctx.current.clearRect(0, 0, initialXEnd + 10, height); // clear the space
      ctx.current.moveTo(initialX, topOffset);
      ctx.current.lineTo(initialXEnd, topOffset);
      ctx.current.lineTo(initialXEnd, availableHeight - (textHeight / 2));
      ctx.current.lineTo(initialX, availableHeight - (textHeight / 2));
      ctx.current.stroke();

      ctx.current.fillStyle = color;
      ctx.current.font = font;

      // text top
      ctx.current.fillText(
        legend.max,
        initialX - 2 - getLabelWidth(legend.max),
        topOffset + (textHeight / 2),
      );

      // text bottom
      ctx.current.fillText(
        legend.min,
        initialX - 2 - getLabelWidth(legend.min),
        availableHeight,
      );

      // text middle
      ctx.current.save();
      ctx.current.textAlign = 'center';
      ctx.current.textBaseline = 'middle';
      ctx.current.translate(initialX - 5, availableHeight / 2);
      ctx.current.rotate(Math.PI / -2);
      ctx.current.fillText(legend.text, 0, 0);
      ctx.current.restore();
    }

    if (orientation === 'vertical') {
      // lines
      const initialY = legend.position - textHeight;
      const initialYEnd = initialY - 5;
      const initialX = 0 + diameter;
      ctx.current.clearRect(0, initialYEnd - 10, width, height); // clear the space
      ctx.current.moveTo(initialX, initialY);
      ctx.current.lineTo(initialX, initialYEnd);
      ctx.current.lineTo(availableWidth, initialYEnd);
      ctx.current.lineTo(availableWidth, initialY);
      ctx.current.stroke();

      ctx.current.fillStyle = color;
      ctx.current.font = font;

      // text right
      ctx.current.save();
      ctx.current.textAlign = 'right';
      ctx.current.textBaseline = 'middle';
      ctx.current.translate(availableWidth, initialY + 15 + textHeight);
      ctx.current.rotate(Math.PI / -2);
      ctx.current.fillText(legend.max, 25, 0);
      ctx.current.restore();

      // text left
      ctx.current.save();
      ctx.current.textAlign = 'right';
      ctx.current.textBaseline = 'middle';
      ctx.current.translate(7, initialY + 15 + textHeight);
      ctx.current.rotate(Math.PI / -2);
      ctx.current.fillText(legend.min, 25, 0);
      ctx.current.restore();

      // text middle
      ctx.current.save();
      ctx.current.textAlign = 'center';
      ctx.current.fillText(
        legend.text,
        (availableWidth / 2),
        initialY + 2 + textHeight,
      );
      ctx.current.restore();
    }
  }, [
    availableHeight, availableWidth, color, font, getLabelWidth,
    height, legend, orientation, textHeight, topOffset, width,
  ]);

  const drawTreeFromLeafs = useCallback((hoveredItems?: DistanceTreeItem[]) => {
    if (!ctx.current) return;

    ctx.current.clearRect(0, 0, width, height);

    nodes.current = [];

    // Draw end leafs as it's the only items having a position defined
    items.forEach((item) => {
      if (item.position === undefined) return;

      let x = orientation === 'horizontal' ? item.position : 0;
      if (orientation === 'vertical' && direction === 'default') {
        x = (availableWidth * (item.distance / 100));
      }
      if (orientation === 'vertical' && direction === 'opposite') {
        x = availableWidth - (availableWidth * (item.distance / 100));
      }

      let y = orientation === 'vertical' ? item.position : 0;
      if (orientation === 'horizontal' && direction === 'default') {
        y = topOffset + availableHeight - (availableHeight * (item.distance / 100));
      }
      if (orientation === 'horizontal' && direction === 'opposite') {
        y = (availableHeight * (item.distance / 100));
      }

      drawNode(x, y);
      const nodePath = drawNode(x, y, true);
      if (!nodePath) return;
      const node: Node = {
        item, x, y, nodePath,
      };
      nodes.current.push(node);
      if (endLinesToEdge) drawLine(node, undefined);

      if (item.label && showLabel) {
        const rectanglePath = drawLabel(node, hoveredItems?.some((i) => i.id === node.item.id) || false);
        node.leafPath = rectanglePath;
      }
    });

    // Then from end leafs positions go up until the root node
    items.forEach((item) => {
      if (item.position === undefined) return;

      if (item.parentId === undefined) return;
      const parentItem = items.find((i) => i.id === item.parentId);

      if (parentItem) createItem(parentItem, hoveredItems);
    });

    drawLegend();
  }, [
    availableHeight, availableWidth, createItem, direction, drawLabel, drawLine,
    drawNode, height, items, orientation, showLabel, width, drawLegend, topOffset, endLinesToEdge,
  ]);

  useEffect(() => {
    if (canvasRef.current && availableWidth !== 0 && availableHeight !== 0) {
      ctx.current = canvasRef.current.getContext('2d');

      if (ctx.current) {
        ctx.current.clearRect(0, 0, width, height);
        ctx.current.save();

        nodes.current = [];

        drawTreeFromLeafs();
      }
    }
  }, [availableHeight, availableWidth, canvasRef, drawTreeFromLeafs, height, width]);

  const debouncedHandleMouseClick = useDebouncedCallback((event: MouseEvent) => {
    if (!onClick || !canvasRef.current) return;

    const { x, y } = getCursorPosition(canvasRef.current, event);

    nodes.current.forEach((node) => {
      if (!ctx.current) return;

      if (ctx.current.isPointInPath(node.nodePath, x, y)) {
        if (onClick) onClick(node.item);
      }
      if (node.leafPath && ctx.current.isPointInPath(node.leafPath, x, y)) {
        if (onClick) onClick(node.item);
      }
    });
  }, 50);

  const debouncedHandleMouseMove = useDebouncedCallback((event: MouseEvent) => {
    if (!canvasRef.current) return;
    drawTreeFromLeafs();

    const { x, y } = getCursorPosition(canvasRef.current, event);

    nodes.current.forEach((node) => {
      if (ctx.current && ctx.current.isPointInPath(node.nodePath, x, y)) {
        if (onHover) onHover(node.item);

        // Re-draw with hover effect
        const hoveredItems = getDistanceTreeItemTree(items, node.item);
        drawTreeFromLeafs(hoveredItems);
      }
    });
  }, 50);

  useEffect(() => {
    const el = canvasRef.current;
    el?.addEventListener('click', debouncedHandleMouseClick);
    el?.addEventListener('mousemove', debouncedHandleMouseMove);

    return () => {
      el?.removeEventListener('click', debouncedHandleMouseClick);
      el?.removeEventListener('mousemove', debouncedHandleMouseMove);
    };
  }, [canvasRef, debouncedHandleMouseClick, debouncedHandleMouseMove]);

  return (
    <canvas id={id} ref={canvasRef} width={width} height={height} />
  );
}

export default DistanceTree;
